import { useEffect, useState } from "react";
import { api, serviceLabel } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, X, Handshake } from "lucide-react";

const MAX = 6;

export const ReferralEditor = ({ business }) => {
  const [vendors, setVendors] = useState([]);
  const [picks, setPicks] = useState(business.referrals || []);
  const [choice, setChoice] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/businesses").then(({ data }) => setVendors(data)).catch(() => {}); }, []);
  const byId = Object.fromEntries(vendors.map((v) => [v.id, v]));
  const available = vendors.filter((v) => !picks.some((p) => p.business_id === v.id));

  const add = () => {
    if (!choice) return;
    if (picks.length >= MAX) { toast.error(`Up to ${MAX} vendors`); return; }
    setPicks((p) => [...p, { business_id: choice, note: "" }]); setChoice("");
  };
  const setNote = (id, note) => setPicks((p) => p.map((r) => (r.business_id === id ? { ...r, note } : r)));
  const remove = (id) => setPicks((p) => p.filter((r) => r.business_id !== id));
  const save = async () => {
    setSaving(true);
    try { await api.put("/businesses/me/referrals", { referrals: picks }); toast.success("Recommendations saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); } finally { setSaving(false); }
  };

  return (
    <div className="card-soft p-8 mt-6" data-testid="referral-editor">
      <div className="flex items-center gap-2"><Handshake className="w-5 h-5 text-[color:var(--emerald)]" />
        <h3 className="font-display text-xl font-bold text-[color:var(--navy)]">Recommended Pros</h3>
        <span className="text-xs text-[color:var(--ink-3)] ml-auto" data-testid="referral-count">{picks.length}/{MAX}</span></div>
      <p className="text-sm text-[color:var(--ink-2)] mt-1">Pick up to {MAX} trusted vendors to show on your directory profile, with a short note for your clients.</p>
      <div className="flex gap-2 mt-5">
        <select value={choice} onChange={(e) => setChoice(e.target.value)} data-testid="referral-vendor-select" className="flex-1 h-11 rounded-lg border border-slate-300 px-3 bg-white text-sm">
          <option value="">{available.length ? "Choose an approved vendor..." : vendors.length ? "All approved vendors added" : "No approved vendors yet"}</option>
          {available.map((v) => <option key={v.id} value={v.id}>{v.name} · {serviceLabel(v.category)} · {v.city}</option>)}
        </select>
        <Button onClick={add} disabled={!choice || picks.length >= MAX} data-testid="referral-add-button" className="btn-primary rounded-full"><Plus className="w-4 h-4" /> Add</Button>
      </div>
      <div className="space-y-3 mt-4">
        {picks.map((r) => (
          <div key={r.business_id} className="flex items-center gap-3 p-3 rounded-xl bg-[color:var(--bg-3)]" data-testid={`referral-item-${r.business_id}`}>
            <div className="w-44 shrink-0 text-sm font-semibold text-[color:var(--navy)] truncate">{byId[r.business_id]?.name || "Vendor"}</div>
            <Input value={r.note} maxLength={200} onChange={(e) => setNote(r.business_id, e.target.value)} placeholder="Why you recommend them (optional)" data-testid={`referral-note-${r.business_id}`} className="bg-white" />
            <button onClick={() => remove(r.business_id)} data-testid={`referral-remove-${r.business_id}`} className="text-[color:var(--ink-3)] hover:text-red-600"><X className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <Button onClick={save} disabled={saving} data-testid="referral-save-button" className="btn-amber rounded-full mt-5 font-semibold">{saving ? "Saving..." : "Save Recommendations"}</Button>
    </div>
  );
};
